import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import { ComponentPropsWithoutRef, ComponentType, useLayoutEffect, useRef, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { useTranslation } from '@/hooks/useTranslation';
import { countFittingItems } from '@/pages/dam/utils/countFittingItems';
import { cn } from '@/services/utils';

/** Matches `tw-gap-2` of the row. */
const CHIP_GAP_PX = 8;

interface DamFilterChip {
    id: string;
    label: string;
    icon?: ComponentType<ComponentPropsWithoutRef<'svg'> & { title?: string }>;
}

interface DamFilterChipsProps {
    chips: DamFilterChip[];
    onRemove: (id: string) => void;
    onClear: () => void;
}

const chipButtonClassName = cn(
    'tw-m-0 tw-inline-flex tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-transparent tw-p-0',
    'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2',
);

function ChipBadge({ chip }: { chip: DamFilterChip }) {
    return (
        <Badge variant="neutral" size="xl" icon={chip.icon}>
            <span className="tw-inline-flex tw-items-center tw-gap-1">
                {chip.label}
                <XMarkIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0 tw-icon-primary" />
            </span>
        </Badge>
    );
}

/**
 * Active filters below the search row (wireframe `I6392:31624;6379:33498;6473:41984`).
 * One line; chips that do not fit collapse into "+N", which expands the row.
 */
function DamFilterChips({ chips, onRemove, onClear }: DamFilterChipsProps) {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(chips.length);
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const clearRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) {
            return undefined;
        }

        const update = () => {
            const children = Array.from(measure.children) as HTMLElement[];
            const moreWidth = children[children.length - 1]?.offsetWidth ?? 0;
            const widths = children.slice(0, -1).map((child) => child.offsetWidth);
            const available = container.clientWidth - (clearRef.current?.offsetWidth ?? 0) - CHIP_GAP_PX;
            setVisibleCount(countFittingItems(available, widths, moreWidth, CHIP_GAP_PX));
        };

        update();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(update);
        observer.observe(container);
        return () => observer.disconnect();
    }, [chips]);

    if (chips.length === 0) {
        return null;
    }

    const hiddenCount = chips.length - visibleCount;
    const shownChips = isExpanded ? chips : chips.slice(0, visibleCount);

    return (
        <div ref={containerRef} className="tw-relative tw-flex tw-min-w-0 tw-items-center tw-justify-center tw-gap-2">
            <div className="tw-min-w-0">
                <div ref={measureRef} aria-hidden className="tw-pointer-events-none tw-invisible tw-absolute tw-flex tw-gap-2 tw-whitespace-nowrap">
                    {chips.map((chip) => <ChipBadge key={chip.id} chip={chip} />)}
                    <Badge variant="neutral" size="xl">{t('DAM.LIBRARY.MORE_FILTERS', { count: chips.length })}</Badge>
                </div>
                <ul className={cn('tw-m-0 tw-flex tw-list-none tw-justify-center tw-gap-2 tw-p-0', isExpanded ? 'tw-flex-wrap' : 'tw-flex-nowrap tw-overflow-hidden')}>
                    {shownChips.map((chip) => (
                        <li key={chip.id} className="tw-flex">
                            <button
                                type="button"
                                className={chipButtonClassName}
                                aria-label={t('DAM.LIBRARY.REMOVE_FILTER', { label: chip.label })}
                                onClick={() => onRemove(chip.id)}
                            >
                                <ChipBadge chip={chip} />
                            </button>
                        </li>
                    ))}
                    {!isExpanded && hiddenCount > 0 && (
                        <li className="tw-flex">
                            <button
                                type="button"
                                className={chipButtonClassName}
                                aria-label={t('DAM.LIBRARY.SHOW_ALL_FILTERS')}
                                onClick={() => setIsExpanded(true)}
                            >
                                <Badge variant="neutral" size="xl">{t('DAM.LIBRARY.MORE_FILTERS', { count: hiddenCount })}</Badge>
                            </button>
                        </li>
                    )}
                </ul>
            </div>
            <div ref={clearRef} className="tw-shrink-0">
                <Button variant="ghost" onClick={onClear}>
                    {t('DAM.LIBRARY.CLEAR_FILTERS')}
                </Button>
            </div>
        </div>
    );
}

export { DamFilterChips };
export type { DamFilterChip, DamFilterChipsProps };
